import { useEffect, useRef, useState } from 'react';

export default function FieldVideoSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: 'ri-map-pin-2-line', value: 'PDVSA', desc: 'Instalaciones certificadas' },
    { icon: 'ri-drop-line', value: 'Fluidos', desc: 'de perforación activos' },
    { icon: 'ri-tools-line', value: 'In-situ', desc: 'Aplicación directa en campo' },
    { icon: 'ri-shield-check-line', value: 'Control', desc: 'técnico en tiempo real' },
  ];

  return (
    <section ref={sectionRef} className="w-full py-20 md:py-28 bg-background-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-px bg-accent-500" />
            <span className="text-accent-600 text-xs font-semibold tracking-widest uppercase">
              Operaciones en Campo
            </span>
            <div className="w-10 h-px bg-accent-500" />
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">
            Nuestros químicos,{' '}
            <span className="text-primary-500">en acción real</span>
          </h2>
          <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Esto no es simulación. Así se ve nuestra química cuando llega al campo —
            fluidos activos, equipos en operación y resultados que se miden en producción.
          </p>
        </div>

        {/* Main layout */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-center">

          {/* Left: Image gallery replacing video */}
          <div className={`w-full lg:w-[58%] flex-shrink-0 transition-all duration-800 delay-150 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {/* Main large image */}
            <div className="relative rounded-2xl overflow-hidden bg-foreground-950 border border-background-200 group">
              <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/50f4980ba85735ba5fe3838203eb7c27.jpeg"
                  alt="Fluido de perforación en mezcla activa en campo petrolero"
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                {/* Badge */}
                <div className="absolute top-4 left-4 z-10">
                  <span className="px-3 py-1.5 rounded-full bg-accent-500 text-white text-xs font-semibold flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse inline-block"></span>
                    Operación en Campo
                  </span>
                </div>
                {/* Bottom caption */}
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-semibold text-sm md:text-base">Tratamiento de fluidos — Campo real</p>
                  <p className="text-white/60 text-xs mt-1">Poly Alphas Chemicals C.A. · Operaciones activas</p>
                </div>
              </div>
            </div>

            {/* Image strip below */}
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div className="relative rounded-xl overflow-hidden h-24 group cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/50f4980ba85735ba5fe3838203eb7c27.jpeg"
                  alt="Fluido de perforación en mezcla activa"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30" />
                <span className="absolute bottom-2 left-2 text-white text-[10px] font-semibold">Lodo activo</span>
              </div>
              <div className="relative rounded-xl overflow-hidden h-24 group cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/20ef1e4b1c6fc3deb4822882313044ed.jpeg"
                  alt="Mezcla de fluidos con agitadores"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30" />
                <span className="absolute bottom-2 left-2 text-white text-[10px] font-semibold">Agitación</span>
              </div>
              <div className="relative rounded-xl overflow-hidden h-24 group cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/c86c4617ec100409b4628851bf05d2a9.jpeg"
                  alt="Instalaciones PDVSA con tanques"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/30" />
                <span className="absolute bottom-2 left-2 text-white text-[10px] font-semibold">Campo PDVSA</span>
              </div>
            </div>
          </div>

          {/* Right content */}
          <div className={`flex-1 transition-all duration-800 delay-250 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3 mb-7">
              {stats.map((s) => (
                <div key={s.value} className="p-4 rounded-xl bg-background-100 border border-background-200 flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className={`${s.icon} text-accent-600 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-foreground-900 text-sm font-semibold">{s.value}</p>
                    <p className="text-foreground-500 text-xs mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quote block */}
            <div className="p-6 rounded-2xl bg-foreground-950 text-white mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500/20">
                  <i className="ri-double-quotes-l text-accent-400 text-sm"></i>
                </div>
                <span className="text-accent-400 text-xs font-semibold tracking-widest uppercase">Operación Directa</span>
              </div>
              <p className="text-foreground-200 text-sm leading-relaxed mb-3">
                Nuestros técnicos se desplazan directamente a las instalaciones para supervisar
                la aplicación, ajustar dosis y garantizar que cada litro de producto rinda
                al máximo en condiciones reales.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {['Fluidos de perforación', 'Tratamiento en pozo', 'Supervisión técnica', 'Dosificación en campo'].map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-accent-500/15 text-accent-300 border border-accent-500/25">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Result items */}
            <ul className="space-y-3">
              {[
                { icon: 'ri-checkbox-circle-line', text: 'Reducción de viscosidad excesiva en lodos de perforación', color: 'text-primary-500' },
                { icon: 'ri-checkbox-circle-line', text: 'Control de filtrado y pérdida de fluidos en formación', color: 'text-primary-500' },
                { icon: 'ri-checkbox-circle-line', text: 'Inhibición de arcillas y lutitas reactivas', color: 'text-primary-500' },
                { icon: 'ri-checkbox-circle-line', text: 'Estabilidad del pozo durante la perforación', color: 'text-primary-500' },
              ].map((item) => (
                <li key={item.text} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} ${item.color} text-base`}></i>
                  </div>
                  <span className="text-foreground-600 text-sm leading-snug">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}